/*
 * <BeTabView> -- React wrapper for .be-tab-view (components/tab-view.scss).
 * Controlled (`selected` + `onSelect`) or uncontrolled (starts on the first tab).
 * The strip is a `focusgroup` tablist: one tab stop, Left/Right/Home/End between
 * tabs, selecting as they go -- Microsoft's polyfill until browsers ship it.
 */

import { polyfill } from "@microsoft/focusgroup-polyfill/shadowless";
import { type ReactNode, useId, useLayoutEffect, useRef, useState } from "react";
import { type BeProps, beComponent } from "./create.tsx";
import { pixelText } from "./pixel-text.tsx";

export type BeTab = { id: string; label: ReactNode };

export type BeTabViewProps = BeProps<
	"div",
	{
		tabs: BeTab[];
		/** Selected tab id (controlled). Omit for uncontrolled. */
		selected?: string;
		/** Called with the clicked tab id. */
		onSelect?: (id: string) => void;
		children?: ReactNode;
		/** Stretch the tabs to fill the content width (.be-tab-view--fit-all). */
		fitAll?: boolean;
		/** Wrap tabs onto stacked rows when they overflow (.be-tab-view--multi-line). */
		multiLine?: boolean;
		/**
		 * Take the box this is mounted in, and give the content pane whatever the
		 * tab strip leaves -- so a pane's child has a bounded height to scroll in.
		 */
		fill?: boolean;
		/**
		 * Mount the pane's content against the frame, for content that draws its own
		 * chrome (a scroll view's bars). One pixel, not zero: the pane's frame is an
		 * inset shadow and the selected tab's seam meets its top edge.
		 */
		flush?: boolean;
	}
>;

export const BeTabView = beComponent<BeTabViewProps, HTMLDivElement>("BeTabView", (props, be) => {
	const { tabs, selected, onSelect, children, scale, fitAll, multiLine, fill, flush, className, style, ...rest } = props;
	const baseId = useId();
	const [internal, setInternal] = useState(tabs[0]?.id);
	const active = selected ?? internal;

	const root = be("be-tab-view", fitAll && "be-tab-view--fit-all", multiLine && "be-tab-view--multi-line", fill && "is-fill");
	const contentClass = ["be-tab-view__content", flush ? "is-flush" : null].filter(Boolean).join(" ");

	const [rowLayout, setRowLayout] = useState<string[][]>([]);
	const measureContainerRef = useRef<HTMLDivElement>(null);
	const measureRefs = useRef(new Map<string, HTMLElement>());
	const tablistRef = useRef<HTMLDivElement>(null);
	const refocus = useRef<string | null>(null);

	const select = (id: string) => {
		if (id === active) return;
		const rowOf = (tabId?: string) => rowLayout.findIndex((row) => tabId !== undefined && row.includes(tabId));
		// multi-line moves the selected tab's row to the bottom, and that DOM move drops focus
		if (multiLine && tablistRef.current?.contains(document.activeElement) && rowOf(id) !== rowOf(active)) refocus.current = id;
		if (selected === undefined) setInternal(id);
		onSelect?.(id);
	};

	useLayoutEffect(() => {
		if (!multiLine || !measureContainerRef.current) return;

		const updateRows = () => {
			if (measureRefs.current.size === 0) return;

			const newRows: string[][] = [];
			let currentRow: string[] = [];
			let lastY = -1;

			for (const tab of tabs) {
				const el = measureRefs.current.get(tab.id);
				if (!el) continue;

				const y = el.offsetTop;
				if (lastY !== -1 && Math.abs(y - lastY) > 2) {
					newRows.push(currentRow);
					currentRow = [];
				}
				lastY = y;
				currentRow.push(tab.id);
			}
			if (currentRow.length > 0) newRows.push(currentRow);

			// Bring the entire row containing the active tab to the bottom
			const activeRowIdx = active ? newRows.findIndex((row) => row.includes(active)) : -1;
			let finalRows = newRows;
			if (activeRowIdx !== -1 && newRows.length > 1) {
				const activeRow = newRows[activeRowIdx];
				if (activeRow) {
					finalRows = [...newRows.slice(0, activeRowIdx), ...newRows.slice(activeRowIdx + 1), activeRow];
				}
			}

			// Compare with current rowLayout to avoid infinite render loops
			setRowLayout((prev) => {
				if (prev.length !== finalRows.length) return finalRows;
				for (let i = 0; i < prev.length; i++) {
					const pRow = prev[i];
					const fRow = finalRows[i];
					if (!pRow || !fRow || pRow.length !== fRow.length) return finalRows;
					for (let j = 0; j < pRow.length; j++) {
						if (pRow[j] !== fRow[j]) return finalRows;
					}
				}
				return prev;
			});
		};

		updateRows();
		const ro = new ResizeObserver(updateRows);
		ro.observe(measureContainerRef.current);
		return () => ro.disconnect();
	}, [multiLine, active, tabs]);

	// ponytail: the polyfill disconnects a group only when its owner is the removed node, so an
	// unmounted BeTabView leaks its tablist (microsoft/polyfills#72); FocusGroup + disconnect() if it matters
	useLayoutEffect(() => {
		if (tablistRef.current) polyfill(tablistRef.current);
	}, []);

	// biome-ignore lint/correctness/useExhaustiveDependencies: only the row move drops focus, one render after the selection
	useLayoutEffect(() => {
		if (!refocus.current) return;
		document.getElementById(`${baseId}-tab-${refocus.current}`)?.focus();
		refocus.current = null;
	}, [rowLayout]);

	/*
	 * The tab label is a coverage run and needs the pixel treatment, but the
	 * <button> paints the tab's own bevel and face — pixel-perfect.css documents
	 * that .be-pixel-text must never go on an element with a background — so the
	 * class goes on an inner span.
	 *
	 * Shared with the hidden measure copy ON PURPOSE. .be-pixel-text is
	 * display:inline-block, so wrapping the visible label and not the measured
	 * one would make measured tab widths disagree with rendered ones and mis-pack
	 * the multi-line rows. One function, so they cannot drift apart.
	 */
	const tabLabel = (tab: BeTab) => pixelText(tab.label);

	const renderTab = (tab: BeTab) => {
		const isSel = tab.id === active;

		return (
			<button
				key={tab.id}
				type="button"
				role="tab"
				id={`${baseId}-tab-${tab.id}`}
				aria-selected={isSel}
				aria-controls={`${baseId}-panel`}
				focusgroupstart={isSel ? "" : undefined}
				className="be-tab-view__tab"
				onClick={() => select(tab.id)}
				onFocus={() => select(tab.id)}
			>
				{tabLabel(tab)}
			</button>
		);
	};

	return (
		<div {...rest} {...root}>
			{multiLine && (
				<div className="be-tab-view__measure" ref={measureContainerRef} aria-hidden="true">
					{tabs.map((tab) => (
						<button
							key={tab.id}
							ref={(el) => {
								if (el) measureRefs.current.set(tab.id, el);
							}}
							className="be-tab-view__tab"
							type="button"
						>
							{tabLabel(tab)}
						</button>
					))}
				</div>
			)}
			<div className="be-tab-view__tabs" role="tablist" focusgroup="tablist nomemory" ref={tablistRef}>
				{!multiLine || rowLayout.length === 0
					? tabs.map((tab) => renderTab(tab))
					: rowLayout.map((rowIds, _i) => {
							return (
								<div key={rowIds.join("-")} className="be-tab-row" role="presentation">
									{rowIds.map((id) => {
										const tab = tabs.find((t) => t.id === id);
										if (!tab) return null;
										return renderTab(tab);
									})}
								</div>
							);
						})}
			</div>
			<div className={contentClass} role="tabpanel" id={`${baseId}-panel`} aria-labelledby={`${baseId}-tab-${active}`}>
				{children}
			</div>
		</div>
	);
});
